import React from "react";

export const Tooltip = ({
  content,
  children,
  id,
  placement = "top",
  className = "",
  autoHideMs = 6000,
  show= true, 
  delay = 3000, // delay in milliseconds before showing tooltip
}) => {
  const tooltipId = React.useMemo(
    () => id || `tooltip-${Math.random().toString(36).slice(2, 9)}`,
    [id]
  );

  const wrapperRef = React.useRef(null);
  const [internalOpen, setInternalOpen] = React.useState(false);
  const delayTimerRef = React.useRef(null);

  // show prop controls visibility, internalOpen controls hover/focus state
  const open = show !== false && internalOpen;

  // detect touch-capable devices (guard for SSR)
  const isTouch = React.useMemo(() => {
    if (typeof window === "undefined") return false;
    try {
      return (navigator.maxTouchPoints || 0) > 0 || "ontouchstart" in window;
    } catch (e) {
      return false;
    }
  }, []);

  // auto-hide timer for mobile when opened via tap
  React.useEffect(() => {
    if (!open || !isTouch || !autoHideMs) return;
    const t = setTimeout(() => setInternalOpen(false), autoHideMs);
    return () => clearTimeout(t);
  }, [open, isTouch, autoHideMs]);

  // close on outside click / escape
  React.useEffect(() => {
    if (!open) return;
    function onDocClick(e) {
      if (!wrapperRef.current) return;
      const target = e.target
      if (target && !wrapperRef.current.contains(target)) setInternalOpen(false);
    }
    function onEsc(e) {
      if (e.key === "Escape") setInternalOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("touchstart", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("touchstart", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  // Clear delay timer on unmount
  React.useEffect(() => {
    return () => {
      if (delayTimerRef.current) {
        clearTimeout(delayTimerRef.current);
      }
    };
  }, []);

  const handleTriggerClick = (e) => {
    // only toggle on touch devices; on desktop we rely on hover/focus
    if (isTouch) {
      e.preventDefault();
      e.stopPropagation();
      setInternalOpen((v) => !v);
    }
  };

  const handleShowWithDelay = () => {
    if (delayTimerRef.current) {
      clearTimeout(delayTimerRef.current);
    }
    
    if (delay > 0) {
      delayTimerRef.current = setTimeout(() => {
        setInternalOpen(true);
      }, delay);
    } else {
      setInternalOpen(true);
    }
  };

  const handleHide = () => {
    if (delayTimerRef.current) {
      clearTimeout(delayTimerRef.current);
    }
    setInternalOpen(false);
  };

  // clone child and merge handlers while preserving original props
  let child = children;
  if (React.isValidElement(children)) {
    const origProps = (children.props || {})

    const newProps = {
      ...origProps,
      "aria-describedby": tooltipId,
      "aria-expanded": open,
      onClick: (e) => {
        try {
          if (typeof origProps.onClick === "function") origProps.onClick(e);
        } finally {
          handleTriggerClick(e);
        }
      },
      onMouseEnter: (e) => {
        try {
          if (typeof origProps.onMouseEnter === "function") origProps.onMouseEnter(e);
        } finally {
          handleShowWithDelay();
        }
      },
      onMouseLeave: (e) => {
        try {
          if (typeof origProps.onMouseLeave === "function") origProps.onMouseLeave(e);
        } finally {
          handleHide();
        }
      },
      onFocus: (e) => {
        try {
          if (typeof origProps.onFocus === "function") origProps.onFocus(e);
        } finally {
          handleShowWithDelay();
        }
      },
      onBlur: (e) => {
        try {
          if (typeof origProps.onBlur === "function") origProps.onBlur(e);
        } finally {
          handleHide();
        }
      },
    };

    child = React.cloneElement(children, newProps);
  }

  const tooltipVisibleClass = open
    ? "opacity-100 -translate-y-2 pointer-events-auto"
    : "opacity-0 pointer-events-none";

  return (
    <div
      ref={wrapperRef}
    >
      {child}

      {/* Arrow - rendered first so it appears behind */}
      {show && <div
        aria-hidden
        className={`absolute   ${placement === "top" ? "-top-[18px] left-1/2 -translate-x-1/2" : ""
          } ${tooltipVisibleClass} bg-black group-hover:opacity-100 group-hover:-translate-y-2 group-focus:opacity-100 group-focus:-translate-y-2`}
        style={{
          width: 12,
          height: 12,
          transform: `translate(-50%, 0) rotate(45deg) ${open ? 'translateY(4px)' : ''}`,
          boxShadow: "0 10px 30px rgba(99,102,241,0.08)",
          pointerEvents: "none",
        }}
      />}

      {/* Main tooltip - rendered second so it appears on top */}
      {show && <div
        id={tooltipId}
        role="tooltip"
        className={`absolute bg-black  z-50 whitespace-nowrap text-sm px-4 py-2 rounded-2xl  ${placement === "top" ? "-top-12 left-1/2 -translate-x-1/2" : ""
          } ${tooltipVisibleClass} group-hover:opacity-100 group-hover:-translate-y-2 group-focus:opacity-100 group-focus:-translate-y-2`}
        style={{
          color: "white",
          zIndex: 100,
          boxShadow: "0 18px 50px rgba(99,102,241,0.12)",
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
          border: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <div className="flex items-center gap-2  tracking-tight text-sm">
          {content}
        </div>
      </div>}
    </div>
  );
};